// The "Uber reports" queue in Shift Validation.
//
// Why this screen exists. On 26 August 2026 the first v5 driver pay and position
// email never went out. Nothing failed loudly: the seven Uber files for 25 August
// reached Drive, the watcher saw them, the parser loaded 25 evidence rows, 28
// earning rows and 25 quality rows. But v5_reports_ready_v1 asks two questions,
// is the data present AND is the file registered in uber_report_uploads, and no
// automated step writes that register row. Readiness returned false, nothing was
// released, and forty-two drivers heard silence.
//
// That state was computable and computed. It reached no screen. This tab is the
// screen. Every figure comes from v_uber_report_day and v_uber_report_coverage;
// nothing here is calculated in React beyond choosing a colour and a sentence.
//
// Read-only on purpose. Registering a report is what releases driver-facing pay
// email, so it stays a deliberate human act rather than a button next to a table.
import { Fragment, useMemo, useState } from 'react'
import { FileWarning, ChevronRight, CheckCircle2, Clock } from 'lucide-react'
import { Chip, type ChipTone } from '../interviews/Chip'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../lib/shadcn/table'
import { cn } from '../../lib/shadcn/utils'
import { formatZar, formatDate, formatDateTime } from '../../utils/interviews'

export type UberReportDay = {
  report_date: string
  required: number | string | null
  satisfied: number | string | null
  outstanding_required: number | string | null
  outstanding_other: number | string | null
  parsed_not_registered: number | string | null
  on_drive_not_parsed: number | string | null
  registered_no_file: number | string | null
  not_arrived: number | string | null
  first_file_seen_at: string | null
  last_file_seen_at: string | null
  digest_ready: boolean | null
  shifts: number | string | null
  shifts_validated: number | string | null
  uber_earning_rows: number | string | null
  earning_without_shift: number | string | null
  earning_without_shift_zar: number | string | null
  unlinked_uber_ids: number | string | null
  digest_enqueued_at: string | null
  digest_processed_at: string | null
  digest_messages_queued: number | string | null
  digest_outcome: string | null
}

export type UberReportRow = {
  report_date: string
  report_key: string
  display_name: string | null
  sort_order: number | string | null
  required_for_digest: boolean | null
  on_drive: boolean | null
  registered: boolean | null
  data_loaded: boolean | null
  drive_file_name: string | null
  drive_first_seen_at: string | null
  drive_parse_fired_at: string | null
  register_status: string | null
  registered_at: string | null
  verified_at: string | null
  state: string
  blocks_digest: boolean | null
}

const n = (v: number | string | null | undefined): number => {
  const parsed = v == null || v === '' ? 0 : Number(v)
  return Number.isFinite(parsed) ? parsed : 0
}

// One sentence per state, written for the operator who has to act on it rather
// than for whoever wrote the pipeline.
const STATE_COPY: Record<string, { label: string; tone: ChipTone; meaning: string }> = {
  complete: {
    label: 'Complete',
    tone: 'green',
    meaning: 'The file is on Drive, the data is in the database, and the report is registered as submitted.',
  },
  parsed_not_registered: {
    label: 'Parsed, not registered',
    tone: 'red',
    meaning:
      'The data is loaded and correct, but no row registers the report as submitted, so the digest gate treats the day as incomplete and no driver email is released. Registering it is a person’s decision, not a retry.',
  },
  on_drive_not_parsed: {
    label: 'On Drive, not parsed',
    tone: 'amber',
    meaning: 'The file arrived but the parser has not loaded it. Vehicle performance is never parsed by design.',
  },
  registered_no_file: {
    label: 'Registered, no file',
    tone: 'amber',
    meaning: 'Something registered this report but the file is not on Drive, so nothing can be re-parsed from source.',
  },
  not_arrived: {
    label: 'Not arrived',
    tone: 'neutral',
    meaning: 'Uber has not delivered this report for this date yet.',
  },
}

function stateCopy(state: string) {
  return STATE_COPY[state] ?? { label: state, tone: 'neutral' as ChipTone, meaning: '' }
}

const WINDOWS: { id: number; label: string }[] = [
  { id: 7, label: 'Last 7 days' },
  { id: 21, label: 'Last 21 days' },
  { id: 45, label: 'All 45 days' },
]

export function UberReportQueue({
  days,
  reports,
}: {
  days: UberReportDay[]
  reports: UberReportRow[]
}) {
  const [windowDays, setWindowDays] = useState(21)
  const [onlyOutstanding, setOnlyOutstanding] = useState(false)
  const [open, setOpen] = useState<string | null>(null)

  const byDate = useMemo(() => {
    const map = new Map<string, UberReportRow[]>()
    for (const r of reports) {
      const list = map.get(r.report_date)
      if (list) list.push(r)
      else map.set(r.report_date, [r])
    }
    return map
  }, [reports])

  // The window is a count of the most recent dates the view returned, not a date
  // arithmetic here: the view already decides how far back it looks, and doing it
  // twice is how the two disagree.
  const windowed = useMemo(() => days.slice(0, windowDays), [days, windowDays])

  const visible = useMemo(
    () => (onlyOutstanding ? windowed.filter((d) => n(d.outstanding_required) > 0) : windowed),
    [windowed, onlyOutstanding],
  )

  const outstandingCount = useMemo(
    () => windowed.filter((d) => n(d.outstanding_required) > 0).length,
    [windowed],
  )

  // The specific failure this tab was built for, called out above the table so it
  // is not something an operator has to notice in a column.
  const blocked = useMemo(
    () => windowed.filter((d) => n(d.parsed_not_registered) > 0 && n(d.outstanding_required) > 0),
    [windowed],
  )

  return (
    <div className="space-y-3">
      {blocked.length > 0 ? (
        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100">
          <FileWarning className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">
              {blocked.length === 1
                ? 'One date has every figure loaded and is still holding its driver email.'
                : `${blocked.length} dates have every figure loaded and are still holding their driver emails.`}
            </p>
            <p className="text-xs leading-relaxed">
              {blocked.map((d) => formatDate(d.report_date)).join(', ')}. The data is in the database
              and reconciles; what is missing is the row that registers the report as submitted, and
              nothing in the automated path writes it. Until it is written, the digest gate reads the
              day as incomplete and no driver is told anything.
            </p>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {WINDOWS.map((w) => (
          <button
            key={w.id}
            type="button"
            onClick={() => setWindowDays(w.id)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              windowDays === w.id
                ? 'border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                : 'border-border bg-background text-muted-foreground hover:text-foreground',
            )}
          >
            {w.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setOnlyOutstanding((v) => !v)}
          className={cn(
            'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            onlyOutstanding
              ? 'border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
              : 'border-border bg-background text-muted-foreground hover:text-foreground',
          )}
        >
          Outstanding only
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
            {outstandingCount}
          </span>
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-sm text-muted-foreground">
          <CheckCircle2 className="h-8 w-8 opacity-40" />
          Every required Uber report is registered and parsed for this window.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Shift date</TableHead>
                <TableHead className="text-center">Required reports</TableHead>
                <TableHead>Outstanding</TableHead>
                <TableHead className="text-center">Shifts validated</TableHead>
                <TableHead className="text-center">Uber rows</TableHead>
                <TableHead>Earnings with no shift</TableHead>
                <TableHead>Driver email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((d) => {
                const isOpen = open === d.report_date
                const rows = byDate.get(d.report_date) ?? []
                const outstanding = n(d.outstanding_required)
                const noShift = n(d.earning_without_shift)
                const unlinked = n(d.unlinked_uber_ids)
                return (
                  <Fragment key={d.report_date}>
                    <TableRow
                      className="cursor-pointer"
                      onClick={() => setOpen(isOpen ? null : d.report_date)}
                    >
                      <TableCell>
                        <ChevronRight
                          className={cn(
                            'h-4 w-4 text-muted-foreground transition-transform',
                            isOpen && 'rotate-90',
                          )}
                        />
                      </TableCell>
                      <TableCell className="font-medium whitespace-nowrap">
                        {formatDate(d.report_date)}
                      </TableCell>
                      <TableCell className="text-center tabular-nums">
                        {n(d.satisfied)} of {n(d.required)}
                      </TableCell>
                      <TableCell>
                        {outstanding === 0 ? (
                          <Chip tone="green">Nothing outstanding</Chip>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {n(d.parsed_not_registered) > 0 ? (
                              <Chip tone="red">{n(d.parsed_not_registered)} not registered</Chip>
                            ) : null}
                            {n(d.on_drive_not_parsed) > 0 ? (
                              <Chip tone="amber">{n(d.on_drive_not_parsed)} not parsed</Chip>
                            ) : null}
                            {n(d.not_arrived) > 0 ? (
                              <Chip tone="neutral">{n(d.not_arrived)} not arrived</Chip>
                            ) : null}
                            {n(d.registered_no_file) > 0 ? (
                              <Chip tone="amber">{n(d.registered_no_file)} no file</Chip>
                            ) : null}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center tabular-nums">
                        {n(d.shifts_validated)} of {n(d.shifts)}
                      </TableCell>
                      <TableCell className="text-center tabular-nums">
                        {n(d.uber_earning_rows)}
                      </TableCell>
                      <TableCell>
                        {noShift === 0 && unlinked === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {noShift > 0 ? (
                              <Chip tone="amber">
                                {noShift} · {formatZar(d.earning_without_shift_zar)}
                              </Chip>
                            ) : null}
                            {unlinked > 0 ? (
                              <Chip tone="red">{unlinked} unlinked</Chip>
                            ) : null}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[22rem] text-xs">
                        {d.digest_outcome ? (
                          <span className="text-muted-foreground">{d.digest_outcome}</span>
                        ) : d.digest_ready ? (
                          <Chip tone="amber" icon={<Clock className="h-3 w-3" />}>
                            Released, not yet drained
                          </Chip>
                        ) : (
                          <Chip tone="neutral">Never released</Chip>
                        )}
                      </TableCell>
                    </TableRow>

                    {isOpen ? (
                      <TableRow className="bg-muted/40 hover:bg-muted/40">
                        <TableCell colSpan={8} className="p-0">
                          <div className="space-y-3 p-4">
                            <p className="text-xs text-muted-foreground">
                              {n(d.shifts)} shift{n(d.shifts) === 1 ? '' : 's'} recorded,{' '}
                              {n(d.shifts_validated)} validated.{' '}
                              {noShift > 0
                                ? `${noShift} Uber earning row${noShift === 1 ? '' : 's'} belong to a driver with no shift record on this date, carrying ${formatZar(d.earning_without_shift_zar)}. Reports complete is not the same claim as this day being safe to pay from.`
                                : 'Every Uber earning row on this date has a matching shift record.'}
                            </p>
                            <div className="overflow-x-auto rounded-md border border-border bg-card">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Report</TableHead>
                                    <TableHead>State</TableHead>
                                    <TableHead>Required</TableHead>
                                    <TableHead>File on Drive</TableHead>
                                    <TableHead>First seen</TableHead>
                                    <TableHead>Register</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {rows.map((r) => {
                                    const copy = stateCopy(r.state)
                                    return (
                                      <TableRow key={r.report_key}>
                                        <TableCell className="whitespace-nowrap font-medium">
                                          {r.display_name ?? r.report_key}
                                        </TableCell>
                                        <TableCell>
                                          <Chip tone={copy.tone}>{copy.label}</Chip>
                                        </TableCell>
                                        <TableCell>
                                          {r.required_for_digest ? (
                                            <Chip tone={r.blocks_digest ? 'red' : 'green'}>
                                              {r.blocks_digest ? 'Blocking' : 'Satisfied'}
                                            </Chip>
                                          ) : (
                                            <span className="text-xs text-muted-foreground">
                                              Not required
                                            </span>
                                          )}
                                        </TableCell>
                                        <TableCell className="max-w-[20rem] truncate text-xs text-muted-foreground">
                                          {r.drive_file_name ?? '—'}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                                          {formatDateTime(r.drive_first_seen_at)}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                                          {r.register_status ?? 'not registered'}
                                        </TableCell>
                                      </TableRow>
                                    )
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                            {rows.some((r) => r.state === 'parsed_not_registered') ? (
                              <p className="text-xs leading-relaxed text-muted-foreground">
                                {stateCopy('parsed_not_registered').meaning}
                              </p>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
